import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ProjectMode } from '@/lib/api';

const PHASES = [
  'Intake',
  'Validation',
  'Architecture',
  'Decomposition',
  'Implementation',
  'Verification',
  'Convergence',
  'Deployment',
  'Go-Live',
  'Support',
] as const;

interface LifecycleStepperProps {
  currentPhase: number | null;
  onPhaseChange?: (phase: number) => void;
  mode?: ProjectMode | null;
}

function LifecycleStepper({
  currentPhase,
  onPhaseChange,
  mode,
}: LifecycleStepperProps): React.ReactElement {
  const currentIdx = currentPhase !== null ? currentPhase - 1 : -1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Lifecycle Phase
          {mode && (
            <Badge variant="secondary" className="capitalize">
              {mode}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {currentPhase === null ? (
          <p className="text-center text-sm text-text-tertiary">No lifecycle phase set</p>
        ) : (
          <div className="flex items-center gap-1">
            {PHASES.map((phase, i) => {
              const isFuture = i > currentIdx;
              const isClickable = isFuture && onPhaseChange !== undefined;

              return (
                <div key={phase} className="flex flex-1 items-center">
                  <div className="flex flex-1 flex-col items-center">
                    <div
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={
                        isClickable
                          ? (): void => {
                              onPhaseChange(i + 1);
                            }
                          : undefined
                      }
                      onKeyDown={
                        isClickable
                          ? (e: React.KeyboardEvent<HTMLDivElement>): void => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onPhaseChange(i + 1);
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium',
                        i < currentIdx
                          ? 'bg-success text-white'
                          : i === currentIdx
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-surface-elevated text-text-tertiary border border-border',
                        isClickable && 'cursor-pointer hover:ring-2 hover:ring-primary/50'
                      )}
                    >
                      {i + 1}
                    </div>
                    <span
                      className={cn(
                        'mt-1 text-[10px]',
                        i <= currentIdx ? 'text-text-primary' : 'text-text-tertiary'
                      )}
                    >
                      {phase}
                    </span>
                  </div>
                  {i < PHASES.length - 1 && (
                    <div
                      className={cn(
                        'h-0.5 flex-1 mx-1',
                        i < currentIdx ? 'bg-success' : 'bg-border'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { LifecycleStepper };
export type { LifecycleStepperProps };
